import clsx from "clsx"
import { useEffect, useLayoutEffect, useRef } from "react"

import { faIcon } from "@utils/faIcon"

import Skeleton from "@particles/Skeleton/Skeleton"
import TruncatableText from "@particles/TruncatableText/TruncatableText"

import { Icon } from "@atoms/index"

import { useStepStackOptional } from "@molecules/StepStack/StepStack.context"

import styles from "./StepStackItem.module.scss"
import { StepStackItemProps } from "./StepStackItem.types"

export default function StepStackItem(props: StepStackItemProps) {
    const { id, label, state: stateProp, isLoading, _isFocused, _isHovered, isPressed } = props
    const context = useStepStackOptional()
    const buttonRef = useRef<HTMLButtonElement>(null)

    useLayoutEffect(() => {
        if (!context) {
            return
        }
        return context.registerItem(id)
    }, [id, context])

    let state: StepStackItemProps["state"]
    let isClickable: boolean

    if (context) {
        const { orderedIds, currentItemId, maxClickableItemId, errorItemIds } = context
        const myIndex = orderedIds.indexOf(id)
        const currentIndex = orderedIds.indexOf(currentItemId ?? "")
        const isCurrent = id === currentItemId
        const isBefore = myIndex !== -1 && currentIndex !== -1 && myIndex < currentIndex
        const hasError = errorItemIds.has(id)

        state = isCurrent ? "current" : isBefore && hasError ? "error" : isBefore ? "complete" : "incomplete"

        const maxClickableIndex = maxClickableItemId !== null ? orderedIds.indexOf(maxClickableItemId) : -1
        isClickable =
            maxClickableItemId !== null ? myIndex !== -1 && myIndex <= maxClickableIndex && !isCurrent : isBefore
    } else {
        // Standalone usage (e.g. Storybook) — use prop-based state
        state = stateProp ?? "incomplete"
        isClickable = state === "complete" || state === "error"
    }

    useEffect(() => {
        if (state === "current" && buttonRef.current) {
            buttonRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "center",
            })
        }
    }, [state])

    const handleClick = () => {
        if (isClickable && context) {
            context.navigateTo(id)
        }
    }

    let glyph = faIcon({ name: "circle-dashed", style: "solid" })

    if (state === "current") {
        glyph = faIcon({ name: "circle-half-stroke", style: "solid" })
    } else if (state === "complete") {
        glyph = faIcon({ name: "circle-check", style: "solid" })
    } else if (state === "error") {
        glyph = faIcon({ name: "circle-xmark", style: "solid" })
    }

    return (
        <button
            ref={buttonRef}
            className={clsx(styles.base, styles[state ?? "incomplete"], {
                [styles.disabled]: !isClickable,
                [styles.loading]: isLoading,
                [styles.focused]: _isFocused,
                [styles.hovered]: _isHovered,
                [styles.pressed]: isPressed,
            })}
            aria-disabled={!isClickable}
            data-step={state}
            data-loading={isLoading ? "true" : undefined}
            disabled={!isClickable}
            tabIndex={!isClickable || isLoading || (state !== "complete" && state !== "error") ? -1 : 0}
            onClick={handleClick}
        >
            {isLoading ? (
                <Skeleton circle={true} width={14} height={14} />
            ) : (
                <Icon size={14} container="fixedHeight" icon={glyph} className={styles.icon} />
            )}

            {isLoading ? (
                <div className={styles.labelSkeleton}>
                    <Skeleton width="100%" height={10} borderRadius={0} />
                </div>
            ) : (
                <TruncatableText text={label} className={styles.label} tooltipOnTruncate={true} />
            )}
        </button>
    )
}
